var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { applyJobViaAuth, applyJobViaEmail, uploadResume } from '@/api';
import { useConfigContext, useToast } from '@/hoc';
import { useGoogleAuth, useMutation } from '@/hooks';
import { generateZodSchema } from '@/schema/apply-form-validation';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { DynamicFormField, UploadField } from './form';
import { EmailIcon, GoogleIcon, LinkedInOutlineIcon } from './icons';
import { Button, Modal } from './ui';
import { cn } from '@/utils';
import { useLinkedInAuth } from '@/hooks/use-linkedin-auth';
export var ApplyJobModal = function (_a) {
    var isOpen = _a.isOpen, onClose = _a.onClose, jobId = _a.jobId, isPreview = _a.isPreview;
    var _b = useConfigContext().config, applyVia = _b.applyVia, _c = _b.applyFormFields, applyFormFields = _c === void 0 ? [] : _c;
    var _d = useState(function () {
        if (!applyVia) {
            return 'email';
        }
        return applyVia.allowGoogleAuth ? 'google' : applyVia.allowLinkedInAuth ? 'linkedIn' : 'email';
    }), selectedAuth = _d[0], setSelectedAuth = _d[1];
    var _e = useState(null), file = _e[0], setFile = _e[1];
    var _f = useState(null), error = _f[0], setError = _f[1];
    var _g = useState(false), fileError = _g[0], setFileError = _g[1];
    var _h = useState(null), parsedResume = _h[0], setParsedResume = _h[1];
    var showToast = useToast().showToast;
    var _j = useMutation({
        mutationFn: applyJobViaAuth,
        onError: function (error) {
            showToast({
                message: error.message,
                status: 'error',
            });
        },
        onSuccess: function () {
            showToast({
                message: 'Application submitted successfully.',
                status: 'success',
            });
            onClose();
            setError(null);
        },
    }), applyViaAuthMutation = _j.mutate, isApplyViaAuthLoading = _j.isLoading;
    var applyJobViaGoogleAuth = useCallback(function (googleAuthResponse) {
        if (parsedResume) {
            applyViaAuthMutation({
                account_type: 'candidate',
                job_id: jobId,
                type: 'google-signup-new-user',
                auth_token: googleAuthResponse.accessToken,
                email: googleAuthResponse.user.email,
                fullName: googleAuthResponse.user.name,
                random_id: parsedResume.randomId,
                resume_name: parsedResume.resumeName,
            });
        }
    }, [applyViaAuthMutation, jobId, parsedResume]);
    var applyJobViaLinkedInAuth = useCallback(function (linkedInAuthResponse) {
        var _a;
        if (parsedResume) {
            applyViaAuthMutation({
                account_type: 'candidate',
                job_id: jobId,
                type: 'linkedin-signup-new-user',
                auth_token: linkedInAuthResponse.accessToken,
                email: linkedInAuthResponse.user.email,
                fullName: (_a = linkedInAuthResponse.user) === null || _a === void 0 ? void 0 : _a.name,
                random_id: parsedResume.randomId,
                resume_name: parsedResume.resumeName,
            });
        }
    }, [applyViaAuthMutation, jobId, parsedResume]);
    var _k = useGoogleAuth(applyJobViaGoogleAuth), initiateGoogleAuth = _k.initiateGoogleAuth, isInitialGoogleAuthLoading = _k.isInitialGoogleAuthLoading;
    var _l = useLinkedInAuth(applyJobViaLinkedInAuth), initiateLinkedInAuth = _l.initiateLinkedInAuth, isInitialLinkedInAuthLoading = _l.isInitialLinkedInAuthLoading;
    var schema = generateZodSchema(applyFormFields);
    var _m = useForm({
        resolver: zodResolver(schema),
    }), control = _m.control, register = _m.register, handleSubmit = _m.handleSubmit, _o = _m.formState, errors = _o.errors, isDirty = _o.isDirty, setValue = _m.setValue;
    var _p = useMutation({
        mutationFn: uploadResume,
        onError: function (error) {
            var _a;
            setError((_a = error.message) !== null && _a !== void 0 ? _a : 'An error occurred while submitting the application. Please try again later.');
        },
        onSuccess: function (parsedResume) {
            var _a;
            setParsedResume((_a = parsedResume === null || parsedResume === void 0 ? void 0 : parsedResume.data) !== null && _a !== void 0 ? _a : null);
            if (error) {
                setError(null);
            }
            if (parsedResume === null || parsedResume === void 0 ? void 0 : parsedResume.data.candidateName) {
                var candidateNameField = applyFormFields.find(function (field) { return field.resumeParsedKey === 'candidateName'; });
                candidateNameField &&
                    setValue(candidateNameField.name, parsedResume === null || parsedResume === void 0 ? void 0 : parsedResume.data.candidateName, {
                        shouldDirty: true,
                        shouldTouch: true,
                    });
            }
            if (parsedResume === null || parsedResume === void 0 ? void 0 : parsedResume.data.contactNo) {
                var contactNoField = applyFormFields.find(function (field) { return field.resumeParsedKey === 'contactNo'; });
                contactNoField &&
                    setValue(contactNoField.name, parsedResume === null || parsedResume === void 0 ? void 0 : parsedResume.data.contactNo, {
                        shouldDirty: true,
                        shouldTouch: true,
                    });
            }
            if (parsedResume === null || parsedResume === void 0 ? void 0 : parsedResume.data.email) {
                var emailField = applyFormFields.find(function (field) { return field.resumeParsedKey === 'email'; });
                emailField &&
                    setValue(emailField.name, parsedResume === null || parsedResume === void 0 ? void 0 : parsedResume.data.email, {
                        shouldDirty: true,
                        shouldTouch: true,
                    });
            }
        },
    }), uploadResumeMutation = _p.mutate, isLoading = _p.isLoading;
    var _q = useMutation({
        mutationFn: applyJobViaEmail,
        onError: function (error) {
            setError(error.message);
        },
        onSuccess: function () {
            showToast({
                message: 'Application submitted successfully.',
                status: 'success',
            });
            onClose();
            setError(null);
        },
    }), applyJobViaEmailMutation = _q.mutate, isLoadingApplyJobViaEmail = _q.isLoading;
    var onSubmit = function (data) {
        if (isPreview) {
            return;
        }
        if (error) {
            setError(null);
        }
        if (selectedAuth !== 'email') {
            return;
        }
        if (!file) {
            setError('Please upload a CV file.');
            setFileError(true);
            return;
        }
        if (parsedResume) {
            applyJobViaEmailMutation(__assign({ account_type: 'candidate', job_id: jobId, random_id: parsedResume.randomId, resume_name: parsedResume.resumeName }, data));
        }
    };
    var handleAuthChange = function (auth) {
        if (!file) {
            setError('Please upload a CV file.');
            setFileError(true);
            return;
        }
        if (auth !== 'email' && !file) {
            setError('Please upload a CV file.');
            setFileError(true);
        }
        setSelectedAuth(auth);
        if (auth === 'google' && (applyVia === null || applyVia === void 0 ? void 0 : applyVia.allowGoogleAuth)) {
            initiateGoogleAuth();
        }
        if (auth === 'linkedin' && (applyVia === null || applyVia === void 0 ? void 0 : applyVia.allowLinkedInAuth)) {
            initiateLinkedInAuth();
        }
    };
    return (_jsx(Modal, { isOpen: isOpen, isClosable: true, footer: _jsx("div", { className: 'flex justify-end w-full py-1', children: _jsx(Button, { form: 'apply-job-form', type: 'submit', disabled: !file && isDirty, isLoading: isLoading ||
                    isLoadingApplyJobViaEmail ||
                    isInitialGoogleAuthLoading ||
                    isInitialLinkedInAuthLoading ||
                    isApplyViaAuthLoading, children: "Apply" }) }), onClose: onClose, containerPadding: 'p-2', modelPadding: 'p-0 vjwMd:p-4', contentWidth: 'w-full vjwMd:max-w-2xl max-w-full h-full vjwMd:h-auto overflow-y-auto', title: 'Apply Job', isPreview: isPreview, children: _jsxs("form", { id: 'apply-job-form', className: 'py-2 px-2 flex flex-col gap-2', onSubmit: handleSubmit(onSubmit), children: [_jsx(UploadField, { label: 'Please attach your CV', onFileUpload: function (file) {
                        if (file) {
                            setFile(file);
                            uploadResumeMutation(file);
                        }
                    }, file: file, setFile: setFile, isLoading: isLoading, fileError: fileError, setFileError: setFileError, className: 'w-full' }), error && selectedAuth !== 'email' && (_jsx("div", { className: 'bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative', children: JSON.stringify(error) })), applyVia && (_jsxs("div", { className: cn('grid grid-cols-1 gap-3', {
                        'vjwMd:grid-cols-2': (applyVia === null || applyVia === void 0 ? void 0 : applyVia.allowGoogleAuth) || (applyVia === null || applyVia === void 0 ? void 0 : applyVia.allowLinkedInAuth),
                        'vjwMd:grid-cols-3': (applyVia === null || applyVia === void 0 ? void 0 : applyVia.allowGoogleAuth) && (applyVia === null || applyVia === void 0 ? void 0 : applyVia.allowLinkedInAuth),
                    }), children: [applyVia.allowGoogleAuth && (_jsxs(Button, { className: 'flex flex-col rounded-md text-sm gap-2', variant: 'secondary', disabled: isLoading, onClick: function (e) {
                                e.preventDefault();
                                handleAuthChange('google');
                            }, children: [_jsx(GoogleIcon, { className: 'size-10' }), "Authenticate via Google"] })), applyVia.allowLinkedInAuth && (_jsxs(Button, { className: 'flex flex-col rounded-md text-sm gap-2', variant: 'secondary', disabled: isLoading, onClick: function (e) {
                                e.preventDefault();
                                handleAuthChange('linkedin');
                            }, children: [_jsx(LinkedInOutlineIcon, { className: 'size-10' }), "Authenticate via LinkedIn"] })), _jsxs(Button, { className: 'flex flex-col rounded-md text-sm gap-2', variant: 'secondary', onClick: function (e) {
                                e.preventDefault();
                                handleAuthChange('email');
                            }, children: [_jsx(EmailIcon, { className: 'size-10' }), "Authenticate via Email"] })] })), selectedAuth === 'email' && (_jsxs(_Fragment, { children: [applyFormFields.map(function (field) { return (_jsx(DynamicFormField, { field: field, control: control, register: register, errors: errors }, field.name)); }), error && (_jsx("div", { className: 'bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative', children: error }))] }))] }) }));
};
